import type { QuoteSubmitPayload } from "./quotePayload";
import type { DepartmentConfig, ExtractedData, SubmitErrorKind } from "./types";

export class QuoteSubmitStatusMapper {
  kind(status: number): SubmitErrorKind | null {
    if (status === 429) return "rate_limited";
    if (status === 403) return "forbidden";
    if (status === 413) return "too_large";
    if (status === 415) return "unsupported_type";
    if (status === 502 || status === 503) return "email_unavailable";
    return null;
  }
}

export class QuoteSubmitFailureMapper {
  kind(message: string): SubmitErrorKind {
    switch (message) {
      case "rate_limited":
      case "forbidden":
      case "too_large":
      case "unsupported_type":
      case "email_unavailable":
        return message;
      default:
        return "network";
    }
  }
}

// Thin API client for the FastAPI backend. apiBase is read from the mount
// element's data-api-base attribute; when empty we operate fully offline.
export class QuoteApi {
  constructor(
    private readonly apiBase: string,
    private readonly submitStatus = new QuoteSubmitStatusMapper(),
  ) {}

  get configured(): boolean {
    return this.apiBase.length > 0;
  }

  async getDepartmentConfig(slug: string): Promise<DepartmentConfig | null> {
    if (!this.configured) return null;
    const res = await fetch(`${this.apiBase}/api/departments/${encodeURIComponent(slug)}/config`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(`config request failed: ${res.status}`);
    return (await res.json()) as DepartmentConfig;
  }

  async extract(department: string, text: string, files: File[]): Promise<ExtractedData> {
    if (!this.configured) throw new Error("API not configured");
    const form = new FormData();
    form.append("department", department);
    form.append("text", text);
    for (const f of files) form.append("files", f, f.name);

    const res = await fetch(`${this.apiBase}/api/extract`, {
      method: "POST",
      body: form,
    });

    if (res.status === 429) throw new Error("rate_limited");
    if (res.status === 403) throw new Error("forbidden");
    if (res.status === 503) throw new Error("extraction_unavailable");
    if (!res.ok) throw new Error(`extract failed: ${res.status}`);

    const data = (await res.json()) as { fields?: ExtractedData } & ExtractedData;
    // Backend returns { fields: {...} }; tolerate a bare object too.
    return data.fields ?? (data as ExtractedData);
  }

  async submitQuote(
    payload: QuoteSubmitPayload,
    files: File[] = [],
  ): Promise<{ refId: string; emailSent: boolean }> {
    if (!this.configured) throw new Error("API not configured");
    const res = await fetch(`${this.apiBase}/api/quote`, this.submitInit(payload, files));
    const mapped = this.submitStatus.kind(res.status);
    if (mapped) throw new Error(mapped);
    if (!res.ok) throw new Error(`submit failed: ${res.status}`);
    return (await res.json()) as { refId: string; emailSent: boolean };
  }

  private submitInit(payload: QuoteSubmitPayload, files: File[]): RequestInit {
    if (files.length === 0) {
      return {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      };
    }
    const form = new FormData();
    form.append("payload", JSON.stringify(payload));
    for (const file of files) form.append("files", file, file.name);
    return { method: "POST", body: form };
  }
}
